// /////////////////////////////////////////////////////////////
// [ファイル] TurquoiseSearchDialog.tsx
// [概要] ターコイズ取引先マスタを検索して 1 件選ぶダイアログ。
//        顧客フォームのターコイズコード追加 (CustomerTurquoiseCodeList) から利用する。
//        検索は既存の GCP 中継関数 turquoise_torihikisaki (Process=Search) を使う。
// /////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import * as Icons from '../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';

const SEARCH_URL = 'https://asia-northeast1-plexiform-bot-455507-h3.cloudfunctions.net/turquoise_torihikisaki';

export interface TurquoiseSearchItem {
  torihikisaki_cd: number | string;
  torihikisaki_name: string;
  torihikisaki_kname?: string;
  post_no?: string;
  address1?: string;
  tel_no?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (item: TurquoiseSearchItem) => void;
  // 既に一覧にあるコード (選択不可として薄く表示する)
  excludeCds?: string[];
}

const TurquoiseSearchDialog: React.FC<Props> = ({ open, onClose, onSelect, excludeCds = [] }) => {
  const [word, setWord] = useState('');
  const [items, setItems] = useState<TurquoiseSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!word.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        SEARCH_URL,
        { Process: 'Search', SearchWord: word.trim(), LIMIT: 30, OFFSET: 0 },
        { timeout: 30000, headers: { 'Content-Type': 'application/json' } },
      );
      let raw = res.data;
      if (typeof raw === 'string') {
        const t = raw.trim();
        const i = t.search(/[[{]/);
        if (i !== -1) raw = JSON.parse(t.substring(i));
      }
      const list: TurquoiseSearchItem[] = raw?.turquoisedata?.items || [];
      setItems(list);
      setSearched(true);
      if (raw?.error) setError(String(raw.error));
    } catch (e: any) {
      setError(e?.message || '検索に失敗しました');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setWord('');
    setItems([]);
    setError(null);
    setSearched(false);
    onClose();
  };

  const excluded = new Set(excludeCds.map((c) => String(c).replace(/^0+/, '') || '0'));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth disableScrollLock>
      <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icons.Search sx={{ fontSize: 18 }} /> ターコイズ取引先を検索
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="取引先名・カナ・コードで検索"
            value={word}
            onChange={(e) => setWord(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <Button onClick={handleSearch} disabled={loading || !word.trim()} startIcon={<Icons.Search />} sx={{ ...STANDARD_CTA_BUTTON_SX, whiteSpace: 'nowrap' }}>
            検索
          </Button>
        </Box>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">検索中...</Typography>
          </Box>
        )}
        {error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>}
        {!loading && searched && items.length === 0 && (
          <Typography variant="body2" color="text.secondary">該当する取引先がありません</Typography>
        )}
        {!loading && items.length > 0 && (
          <Box sx={{ overflowX: 'auto', maxHeight: 420 }}>
            <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { py: 0.4, fontSize: '0.8rem' }, '& .MuiTableCell-head': { fontWeight: 600 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 90 }}>コード</TableCell>
                  <TableCell>取引先名</TableCell>
                  <TableCell>カナ</TableCell>
                  <TableCell>住所</TableCell>
                  <TableCell sx={{ width: 110 }}>電話</TableCell>
                  <TableCell sx={{ width: 70 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it) => {
                  const cd = String(it.torihikisaki_cd);
                  const used = excluded.has(cd.replace(/^0+/, '') || '0');
                  return (
                    <TableRow key={cd} hover sx={used ? { opacity: 0.45 } : undefined}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{cd.padStart(8, '0')}</TableCell>
                      <TableCell>{it.torihikisaki_name}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{it.torihikisaki_kname || ''}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'normal' }}>{it.address1 || ''}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{it.tel_no || ''}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          disabled={used}
                          onClick={() => {
                            onSelect(it);
                            handleClose();
                          }}
                          sx={{ ...STANDARD_TEXT_BUTTON_SX, minWidth: 0, px: 1 }}
                        >
                          {used ? '追加済' : '選択'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={STANDARD_TEXT_BUTTON_SX}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TurquoiseSearchDialog;
